import { DateTime } from "luxon";
import { CoffinType } from "../coffin-type/coffin-type.types";
import { ConfirmDelivery } from "../delivery/delivery.types";
import { Doctor } from "../doctor/doctor.types";
import { FileUpload } from "../file-upload/file-upload.types";
import { Flag } from "../flags/flag.model";
import { GeneratedDocumentType } from "../generated-document-type/generated-document-type.types";
import { Oven } from "../oven/oven.types";
import { PersonSize } from "../person-size/person-size.types";
import { PersonStatus } from "../person-status/person-status.types";
import { ReceivedDocumentType, ReceivedDocumentTypeFilter } from "../received-document-type/received-document-type.types";
import { Undertaker } from "../undertaker/undertaker.types";
import { PersonUrnType } from "../urn-type/urn-type.types";
import { Enums } from "./person.enums";
import { PrintedDocumentTypeEnum } from "./printed-document-type.enum";
import { Verification } from "./verification-document/verification-document.model";

export enum ActionStage {
    Receive = 1,
    Process = 2,
    Schedule = 3,
    Delivery = 4,
    Report = 5
};

export interface Person {
    id: string;
    initials: string;
    fullName: string;
    lastName: string;
    notes?: string;
    lastDeliveryCancelReason?: string;
    specialRequests?: string;
    bodyBagNumber?: string;
    idNumber?: string;
    invoiceNumber?: string;
    occupation?: string;
    price?: number;
    hasRemovedHazardousMaterial: boolean;
    isUrgent: boolean;
    expectedDeliveryDate?: string;
    dateReceived?: string;
    dateCremated?: DateTime;
    dateDeath?: DateTime;
    dateReturned?: DateTime;
    dateDocumentsReceived?: DateTime;
    statusId: number;
    status: PersonStatus;
    sizeId?: number;
    size?: PersonSize;
    coffinTypeId?: number;
    coffinType?: CoffinType;
    undertakerId?: number;
    undertaker?: Undertaker;
    address?: Address;
    urnTypes: PersonUrnType[];
    personInstructions: PersonInstruction[];
    receivedDocuments: ReceivedDocument[];
    generatedDocuments: GeneratedDocument[];
    scheduleId?: number;
    scheduleIndex?: number;
    deliveryId?: number;
    deliveryIndex?: number;
    deliverySignatureId: number;
    deliverySignature?: FileUpload;
    verifications?: Verification[];
    ovenId?: string;
    oven?: Oven;
    undertakerTagNumber?: string;
    registerNumber?: string;
    flag?: Flag;
    flagId?: number;
    reserveFutureSchedule: boolean;
    scheduleDate?: any;
    scheduleShiftNumber?: number;
    requireCourierReferenceNumber: boolean;
    courierReferenceNumber?: string;
};

export interface PersonInstruction {
    personId: string;
    instructionId: number;
    instruction: string;
    isApproved: boolean;
    dateApproved?: DateTime;
    approvedBy?: string;
}

export interface PersonListDto {
    id: string;
    registerNumber: string;
    initials: string;
    fullName: string;
    lastName: string;
    notes?: string;
    idNumber?: string;
    bodyBagNumber?: string;
    dateReceived?: string;
    dateDeath?: string;
    status: { description: string };
    undertaker?: { name: string };
    coffinType?: { description?: string };
    personInstructions: { instruction: string }[];
    receivedDocuments: { documentTypeId: number }[];
    generatedDocuments: { documentTypeId: number; isReceived: boolean }[];
}

export interface ReceivedDocument {
    personId: string;
    dateTime: DateTime;
    documentTypeId: number;
    documentType: ReceivedDocumentType;
};

export interface GeneratedDocument {
    personId: string;
    documentTypeId: number;
    documentType: GeneratedDocumentType;
    dateGenerated?: DateTime;
    isReceived: boolean;
    dateReceived?: DateTime;
    doctorId: number;
    doctor: Doctor;
};

export interface Address {
    id: number;
    houseNumber?: string;
    street?: string;
    suburb?: string;
    city?: string;
    postalCode?: string;
    country?: string;
};

export interface AddressRequest {
    houseNumber?: string;
    street?: string;
    suburb?: string;
    city?: string;
    postalCode?: string;
    country?: string;
};

export interface PersonReceiveRequest {
    id?: string;
    initials: string;
    fullName: string;
    lastName: string;
    idNumber: string;
    dateReceived?: DateTime;
    coffinTypeId?: number;
    hasRemovedHazardousMaterial: boolean;
    isUrgent: boolean;
    expectedDeliveryDate?: DateTime;
    notes?: string;
    undertakerId?: number;
    receivedDocumentTypeIds: number[];
    receivedDocuments: ReceivedDocument[];
    instructionIds: number[];
    personUrnTypes?: PersonUrnType[];
    undertakerTagNumber?: string;
    registerNumber?: string;
    sizeId?: number;
    flagId?: number;
    reserveFutureSchedule: boolean;
    reserveFutureScheduleDate?: DateTime;
    reserveFutureScheduleShift?: number;
    requireCourierReferenceNumber: boolean;
    courierReferenceNumber?: string;
};

export interface CreatePersonReceive {
    receiveDetails: PersonReceiveRequest;
    statusId: number;
};

export interface UpdatePersonReceive extends CreatePersonReceive {
    id: string;
};

export interface PersonProcessRequest {
    dateDeath?: DateTime;
    bodyBagNumber?: string;
    price?: number;
    invoiceNumber?: string;
    address?: AddressRequest;
    occupation?: string;
    specialRequests?: string;
    //sizeId?: number;
    //personUrnTypes?: PersonUrnType[];
    generatedDocuments: GeneratedDocumentRequest[];
};

export interface UpdatePersonProcess extends UpdatePersonReceive {
    processDetails: PersonProcessRequest;
};

export interface PersonScheduleRequest {
    coffinTypeId: number;
    dateCremated: DateTime;
    ovenId: string;
};

export interface UpdatePersonSchedule {
    id: string;
    scheduleDetails: PersonScheduleRequest;
    statusId: number;
};


export interface UpdatePersonReportWithFieldChanges extends UpdatePersonReport{
    note: string;
    fields: string;
    documentTypes: PrintedDocumentTypeEnum[];
}

export interface UpdatePersonReport extends UpdatePersonProcess {
    scheduleDetails: PersonScheduleRequest;
    deliveryDetails: ConfirmDelivery;
}

export interface UpdatePersonStatus {
    personId: string;
    statusId: number;
}

export interface DeliveryRequest {
    dateReturned: DateTime;
    signedImage: File;
};

export interface PersonFilter {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc' | '';
    dateReceivedFrom?: DateTime;
    dateReceivedTo?: DateTime;
    undertakerId?: number;
    doctorId?: number;
    outstandingDocuments: Enums.OutstandingDocumentType[];
    receivedDocumentTypes?: ReceivedDocumentTypeFilter[];
    receivedDocumentTypesIds?: number[];
    query?: string;
    actionStage: ActionStage;
    personStatusId?: number;
    isVerified?: boolean | null;
};

export interface PersonReceivingFormData {
    id?: string;
    initials: string;
    fullName: string;
    lastName: string;
    idNumber: string;
    dateReceived?: DateTime;
    coffinTypeId?: number;
    hasRemovedHazardousMaterial: boolean;
    isUrgent: boolean;
    expectedDeliveryDate?: DateTime;
    notes?: string;
    undertakerId?: number;
    receivedDocumentTypeIds: number[];
    receivedDocuments: ReceivedDocument[];
    instructionIds: number[];
    personUrnTypes?: PersonUrnType[];
    undertakerTagNumber?: string;
    registerNumber?: string;
    sizeId?: number;
    flagId?: number;
    reserveFutureSchedule: boolean;
    reserveFutureScheduleDate?: DateTime;
    reserveFutureScheduleShift?: number;
    requireCourierReferenceNumber: boolean;
    courierReferenceNumber?: string;
};

export interface PersonProcessingFormData {
    dateDeath?: DateTime;
    bodyBagNumber?: string;
    price?: number;
    invoiceNumber?: string;
    address?: AddressRequest;
    occupation?: string;
    specialRequests?: string;
    //personUrnTypes?: PersonUrnType[];
    generatedDocuments: GeneratedDocumentFormData[];
};

export interface GeneratedDocumentRequest {
    documentTypeId: number;
    doctorId?: number;
    isReceived: boolean;
    address?: AddressRequest;
    occupation?: string;
};

export interface GeneratedDocumentFormData {
    documentTypeId: number;
    doctorId?: number;
    isReceived: boolean;
};

export interface GenerateDocument {
    personId: string;
    generatedDocumentDetails: GeneratedDocumentRequest;
};

export interface PersonSchedulingFormData {
    dateCremated: DateTime;
    ovenId: string;
};

export interface PersonDeliveryFormData {
    dateReturned: DateTime;
    signatures: File[];
};

export interface PersonIdNumber {
    personId: string;
    idNumber: string;
}

export interface SelectablePerson extends Person {
    isSelected: boolean;
    allowToggle: boolean;
};

export interface PersonSelectedEvent {
    person: Person;
    isSelected: boolean;
}

export interface UpdatePriceAndInvoiceNumber {
    personId: string;
    price: number;
    invoiceNumber: string;
    fieldsArray?: string[];
    fields?: string;
    documentTypes?: PrintedDocumentTypeEnum[];
    note?: string
}

export interface ExistingIdNumberResponse {
    idExists: boolean;
    idExistsForAnotherUser: boolean;
    creatingEmptyUrn: boolean;
    creatingFullProfile: boolean;
    emptyUrnCreated: boolean;
    fullProfileCreated: boolean;
    person?: Person;
}

export interface PersonCourierNumber {
    personId: string;
    courierReferenceNumber: string;
}

export interface UpdatePersonsCourierNumbers {
    data: PersonCourierNumber[];
}

export interface FieldChangeEntry {
    id?: string;
    personId: string;
    date: DateTime;
    note: string;
    changedFields: string[];
    documents: PrintedDocumentTypeEnum[];
    userId?: string;
    userName?: string;
}

export interface CreateFieldChangeEntry {
    personId: string;
    date: DateTime;
    note: string;
    changedFields: string[];
    documents: PrintedDocumentTypeEnum[];
}

 export interface DocumentToBePrinted {
    documentType: DocumentType;
    quantity: number;
}

